import type {
  BranchCode,
  CreateBranchRequestInput,
  CreatePurchaseOrderInput,
  CreateTransferOrderInput,
  ImportTargetsInput,
  PoStatus,
  ReceivePurchaseOrderInput,
  ReorderSuggestion,
  RequestKind,
  RequestStatus,
  StockTarget,
  Supplier,
  SupplyType,
  Urgency,
} from '@lorsamthai/ops-shared';
import { api } from '../auth/api.js';

export interface StockRow {
  sku: string;
  branch: BranchCode;
  brand: string | null;
  model: string | null;
  size: string | null;
  on_hand: number;
  incoming_po: number;
  incoming_tr: number;
  outgoing_tr: number;
  effective: number;
  /** เฉพาะ DOT ปีปัจจุบัน — เกณฑ์ที่ระบบใช้ตัดสินใจสั่ง */
  on_hand_cy: number;
  effective_cy: number;
  /** ของค้าง DOT ปีเก่า */
  on_hand_old: number;
  next_eta: string | null;
  min_qty: number;
  target_qty: number;
  supply_type: SupplyType;
  avg_daily_sales: number;
  auto_calc: boolean;
}

export async function listStock(params: {
  q?: string;
  branch?: BranchCode;
  below_min?: boolean;
  limit?: number;
  offset?: number;
} = {}): Promise<{ items: StockRow[]; total: number }> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.branch) qs.set('branch', params.branch);
  if (params.below_min) qs.set('below_min', 'true');
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  const suffix = qs.toString() ? `?${qs}` : '';
  return api(`/stock${suffix}`);
}

export interface SkuDetail {
  sku: string;
  targets: StockTarget[];
  effective: {
    sku: string;
    branch: BranchCode;
    on_hand: number;
    incoming_po: number;
    incoming_tr: number;
    outgoing_tr: number;
    effective: number;
    next_eta: string | null;
    po_count: number | null;
    on_hand_cy: number;
    effective_cy: number;
    on_hand_old: number;
  }[];
  pending_pos: Array<{
    po_id: number;
    sku: string;
    qty_ordered: number;
    qty_received: number;
    purchase_orders: Array<{
      id: number;
      po_ref: string | null;
      status: PoStatus;
      dest_branch: BranchCode;
      ordered_at: string | null;
      eta_date_promised: string | null;
      eta_date_revised: string | null;
      stuck_after: string | null;
      supplier_id: number;
    }>;
  }>;
}

export async function getStockDetail(sku: string): Promise<SkuDetail> {
  return api(`/stock/${encodeURIComponent(sku)}`);
}

export async function listSuggestions(params: { action?: string; date?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.action) qs.set('action', params.action);
  if (params.date) qs.set('date', params.date);
  const suffix = qs.toString() ? `?${qs}` : '';
  return api<{ items: ReorderSuggestion[]; total: number; date: string | null }>(
    `/suggestions${suffix}`
  );
}

export async function dismissSuggestion(id: number) {
  return api<{ ok: true }>(`/suggestions/${id}/dismiss`, { method: 'POST' });
}

export interface PurchaseOrderDoc {
  id: number;
  po_ref: string | null;
  supplier_id: number;
  dest_branch: BranchCode;
  status: PoStatus;
  total_qty: number | null;
  ordered_at: string | null;
  eta_date_promised: string | null;
  eta_date_revised: string | null;
  stuck_after: string | null;
  received_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  note: string | null;
  created_at: string;
  purchase_order_items: Array<{
    po_id: number;
    sku: string;
    qty_ordered: number;
    qty_received: number;
    unit_cost: number | null;
  }>;
}

export async function listPurchaseOrders(
  status: 'active' | 'stuck' | 'rollover' | 'cancelled' | 'received' | 'all' = 'active'
) {
  return api<{ items: PurchaseOrderDoc[]; total: number }>(`/purchase-orders?status=${status}`);
}

export async function getPurchaseOrder(id: number) {
  return api<PurchaseOrderDoc>(`/purchase-orders/${id}`);
}

export interface CreatePOConflict {
  error: { code: 'pending_po_conflict'; message: string };
  warnings: Array<{
    sku: string;
    pending_qty: number;
    po_id: number;
    po_ref: string | null;
    ordered_at: string | null;
  }>;
}

export async function createPurchaseOrder(input: CreatePurchaseOrderInput): Promise<PurchaseOrderDoc | CreatePOConflict> {
  try {
    return await api<PurchaseOrderDoc>('/purchase-orders', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  } catch (err) {
    // api() throws on non-2xx; re-fetch with parsed body for 409 conflict
    const e = err as Error & { status?: number; code?: string; details?: unknown };
    if (e.status === 409 && e.code === 'pending_po_conflict') {
      // details isn't populated by our wrapper — re-do the request by hand
      // to get the warnings array cleanly.
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/purchase-orders`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            authorization: `Bearer ${localStorage.getItem('lorsamthai.ops.token') ?? ''}`,
          },
          body: JSON.stringify(input),
        }
      );
      return (await res.json()) as CreatePOConflict;
    }
    throw err;
  }
}

export async function receivePurchaseOrder(id: number, input: ReceivePurchaseOrderInput) {
  return api<{ ok: true; fully_received: boolean }>(`/purchase-orders/${id}/receive`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function markPoStuck(id: number, reason?: string) {
  return api<{ ok: true; stuck_after: string }>(`/purchase-orders/${id}/mark-stuck`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function cancelPurchaseOrder(id: number, reason: string) {
  return api<{ ok: true }>(`/purchase-orders/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export interface TransferOrderDoc {
  id: number;
  tr_ref: string | null;
  from_branch: BranchCode;
  to_branch: BranchCode;
  status: string;
  note: string | null;
  created_at: string;
  shipped_at: string | null;
  received_at: string | null;
  transfer_order_items: Array<{ tr_id: number; sku: string; qty: number }>;
}

export async function listTransferOrders() {
  return api<{ items: TransferOrderDoc[]; total: number }>('/transfer-orders');
}

export async function createTransferOrder(input: CreateTransferOrderInput) {
  return api<TransferOrderDoc>('/transfer-orders', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function shipTransfer(id: number) {
  return api<{ ok: true }>(`/transfer-orders/${id}/ship`, { method: 'POST' });
}

export async function receiveTransfer(id: number) {
  return api<{ ok: true }>(`/transfer-orders/${id}/receive`, { method: 'POST' });
}

export async function listSuppliers() {
  return api<{ items: Supplier[] }>('/suppliers');
}

export async function runNightly() {
  return api<{ ok: true; steps: Array<{ step: string; rows_affected: number }> }>(
    '/admin/run-nightly',
    { method: 'POST' }
  );
}

export async function backfillOrders() {
  return api<{ ok: true; rows_updated: number }>('/admin/backfill-orders', { method: 'POST' });
}

export async function aggregateSales() {
  return api<{ ok: true; rows_inserted: number }>('/admin/aggregate-sales', { method: 'POST' });
}

export async function previewMonthRollover() {
  return api<{ count: number }>('/admin/cancel-month-rollover/preview');
}

export async function cancelMonthRollover() {
  return api<{ ok: true; rows_cancelled: number }>(
    '/admin/cancel-month-rollover',
    { method: 'POST' }
  );
}

export async function listTargetsForSku(sku: string) {
  return api<{ items: StockTarget[] }>(`/stock-targets/${encodeURIComponent(sku)}`);
}

export async function updateStockTarget(
  sku: string,
  branch: BranchCode,
  patch: import('@lorsamthai/ops-shared').UpdateStockTargetInput
) {
  return api<StockTarget>(
    `/stock-targets/${encodeURIComponent(sku)}/${branch}`,
    { method: 'PATCH', body: JSON.stringify(patch) }
  );
}

export async function applySupplierAcrossBranches(sku: string, supplierId: number | null) {
  return api<{ ok: true; updated: number }>(
    `/stock-targets/${encodeURIComponent(sku)}/apply-supplier`,
    { method: 'POST', body: JSON.stringify({ supplier_id: supplierId }) }
  );
}

export interface OrderDayRow {
  sku: string;
  brand: string | null;
  model: string | null;
  size: string | null;
  on_hand_total: number;
  effective_total: number;
  incoming_po_total: number;
  incoming_tr_total: number;
  min_total: number;
  target_total: number;
  velocity_total: number;
  supplier_id: number | null;
  supplier_name: string | null;
  supplier_reliability: 'fast' | 'normal' | 'unreliable' | null;
  suggested_qty: number;
  days_of_cover: number | null;
  /** เฉพาะ DOT ปีปัจจุบัน — suggested_qty และเกณฑ์ "ต่ำกว่า min" คิดจากตัวนี้ */
  on_hand_cy_total: number;
  effective_cy_total: number;
  /** ของค้าง DOT ปีเก่า (ไม่ถูกนับเป็นของที่ตอบโจทย์ min/target) */
  on_hand_old_total: number;
}

export async function listOrderDay() {
  return api<{ items: OrderDayRow[]; total: number }>('/order-day');
}

export async function createOrderDayPos(
  items: Array<{ sku: string; qty: number; supplier_id: number; unit_cost?: number }>,
  note?: string
) {
  return api<{
    ok: true;
    created: Array<{ supplier_id: number; po_id: number; po_ref: string | null; qty_total: number }>;
  }>(`/order-day/create-pos`, {
    method: 'POST',
    body: JSON.stringify({ items, note }),
  });
}

// ---------------------------------------------------------------------------
// Order Day "by branch" — per-(sku, dest_branch) rows below min
// ---------------------------------------------------------------------------
export interface OrderDayByBranchRow {
  sku: string;
  brand: string | null;
  model: string | null;
  size: string | null;
  dest_branch: BranchCode;
  on_hand: number;
  effective: number;
  incoming_po: number;
  incoming_tr: number;
  min_qty: number;
  target_qty: number;
  velocity: number;
  supplier_id: number | null;
  supplier_name: string | null;
  supplier_reliability: 'fast' | 'normal' | 'unreliable' | null;
  /** Qty bp will cover via transfer (0 for bp itself). Fully consumed on any
   *  visible row, so suggested_qty = supplier order for the residual gap. */
  transfer_credit: number;
  /** "โอนก่อน" sizing: supplier order AFTER subtracting bp's transfer credit. */
  suggested_qty: number;
  /** "สั่งตรง" sizing: supplier order for this branch's own gap, bp spare
   *  ignored. > 0 for every branch below its own min. */
  suggested_qty_direct: number;
  days_of_cover: number | null;
  /** เฉพาะ DOT ปีปัจจุบัน — ทั้งสองสูตรและ transfer_credit คิดจากตัวนี้ */
  on_hand_cy: number;
  effective_cy: number;
  /** ของค้าง DOT ปีเก่าของสาขานี้ */
  on_hand_old: number;
}

export async function listOrderDayByBranch() {
  return api<{ items: OrderDayByBranchRow[]; total: number }>('/order-day-by-branch');
}

export async function createOrderDayByBranchPos(
  items: Array<{
    sku: string;
    qty: number;
    supplier_id: number;
    dest_branch: BranchCode;
    unit_cost?: number;
  }>,
  note?: string
) {
  return api<{
    ok: true;
    created: Array<{
      dest_branch: BranchCode;
      supplier_id: number;
      po_id: number;
      po_ref: string | null;
      qty_total: number;
    }>;
  }>(`/order-day-by-branch/create-pos`, {
    method: 'POST',
    body: JSON.stringify({ items, note }),
  });
}

// ---------------------------------------------------------------------------
// Transfer Day (bp → child branches checklist)
// ---------------------------------------------------------------------------
export interface TransferDayRow {
  sku: string;
  brand: string | null;
  model: string | null;
  size: string | null;
  dest_branch: BranchCode;
  on_hand: number;
  effective: number;
  incoming_po: number;
  incoming_tr: number;
  min_qty: number;
  target_qty: number;
  velocity: number;
  bp_effective: number;
  /** ของบางพลีที่โอนได้ — นับเฉพาะ DOT ปีปัจจุบัน */
  bp_avail: number;
  desired_qty: number;
  suggested_qty: number;
  days_of_cover: number | null;
  /** เฉพาะ DOT ปีปัจจุบัน — เกณฑ์ทั้งหมดคิดจากตัวนี้ */
  on_hand_cy: number;
  effective_cy: number;
  /** ของค้าง DOT ปีเก่าของสาขาปลายทาง */
  on_hand_old: number;
  bp_effective_cy: number;
}

export async function listTransferDay() {
  return api<{ items: TransferDayRow[]; total: number }>('/transfer-day');
}

export async function createTransferDayTrs(
  items: Array<{ sku: string; qty: number; dest_branch: BranchCode }>,
  note?: string
) {
  return api<{
    ok: true;
    created: Array<{ dest_branch: BranchCode; tr_id: number; tr_ref: string | null; qty_total: number }>;
  }>(`/transfer-day/create-trs`, {
    method: 'POST',
    body: JSON.stringify({ items, note }),
  });
}

// ---------------------------------------------------------------------------
// Branch requests (Stage 4 — kept for API compatibility, no UI nav surface
// in single-admin mode)
// ---------------------------------------------------------------------------
export interface BranchRequestDoc {
  id: number;
  req_ref: string | null;
  kind: RequestKind;
  from_branch: BranchCode;
  source_branch: BranchCode | null;
  urgency: Urgency;
  status: RequestStatus;
  reason: string | null;
  submitted_by: string;
  submitted_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  linked_tr_id: number | null;
  linked_po_id: number | null;
  branch_request_items: Array<{ sku: string; qty: number; note: string | null }>;
}

export interface BranchRequestDetail extends BranchRequestDoc {
  submitter: { id: string; name: string; branch: BranchCode | null } | null;
  reviewer: { id: string; name: string; branch: BranchCode | null } | null;
  comments: Array<{
    id: number;
    body: string;
    created_at: string;
    author: { id: string; name: string } | null;
  }>;
}

export async function listBranchRequests(params: {
  status?: 'open' | 'closed' | 'all' | RequestStatus;
  kind?: RequestKind;
} = {}) {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.kind) qs.set('kind', params.kind);
  const suffix = qs.toString() ? `?${qs}` : '';
  return api<{ items: BranchRequestDoc[]; total: number }>(`/requests${suffix}`);
}

export async function getBranchRequest(id: number) {
  return api<BranchRequestDetail>(`/requests/${id}`);
}

export async function createBranchRequest(input: CreateBranchRequestInput) {
  return api<BranchRequestDoc>('/requests', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function commentOnRequest(id: number, body: string) {
  return api<{ id: number; body: string; created_at: string }>(`/requests/${id}/comments`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

export interface ReviewResult {
  ok: true;
  status: 'approved' | 'rejected';
  linked_tr_id?: number | null;
  linked_po_id?: number | null;
  warnings?: Array<{ code: string; message: string; sku?: string }>;
}

export async function reviewRequest(id: number, action: 'approve' | 'reject', note?: string) {
  return api<ReviewResult>(`/requests/${id}/review`, {
    method: 'POST',
    body: JSON.stringify({ action, note }),
  });
}

export async function cancelRequest(id: number) {
  return api<{ ok: true; status: 'cancelled' }>(`/requests/${id}/cancel`, { method: 'POST' });
}

// ---------------------------------------------------------------------------
// Stock targets bulk import (CSV template + dry-run + commit)
// ---------------------------------------------------------------------------
export interface ImportTargetsResult {
  ok: true;
  dry_run: boolean;
  inserted: number;
  updated: number;
  skipped: number;
  errors: Array<{ row: number; sku?: string; branch?: string; message: string }>;
}

/**
 * Fetch the CSV template with the bearer token, then trigger a blob download
 * in the browser. We can't use a plain <a href> because the API requires the
 * Authorization header.
 */
export async function downloadTargetsTemplate(
  mode: 'all' | 'configured' | 'unconfigured' = 'all'
): Promise<void> {
  const base = import.meta.env.VITE_API_BASE_URL as string;
  const token = localStorage.getItem('lorsamthai.ops.token') ?? '';
  const res = await fetch(`${base}/stock-targets/template.csv?mode=${mode}`, {
    headers: { authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`download failed: HTTP ${res.status}`);
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const filename =
    cd.match(/filename="([^"]+)"/)?.[1] ?? `stock-targets-${mode}.csv`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function importTargets(input: ImportTargetsInput) {
  return api<ImportTargetsResult>('/stock-targets/import', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}
